// public/scripts/index-tournament.js

// --- Imports ---
import { getFirebaseAuthService, getFunctionsService } from './krida-auth.js';
import { httpsCallable } from "https://www.gstatic.com/firebasejs/10.12.2/firebase-functions.js";
import { refreshMyTournamentsView } from './my-tournaments-client.js';

// --- DOM Element References ---
let createTournamentButton; // Button in the main page header to open the modal
let tournamentModal; // The modal element itself
let tournamentModalContainer; // The container where modal HTML is loaded

// --- Assign DOM elements specific to tournament feature ---
function assignTournamentElements() {
    createTournamentButton = document.getElementById('createTournamentButton');
    tournamentModalContainer = document.getElementById('tournamentModalContainer');
    // tournamentModal will be assigned after its HTML is loaded
}

// --- Tournament Modal Open/Close --- 
function openTournamentModalLocal() {
    const auth = getFirebaseAuthService();
    if (!auth || !auth.currentUser) {
        alert('Please sign in to create a tournament.');
        return;
    }
    if (tournamentModal) {
        tournamentModal.style.display = 'block';
    } else {
        console.error('Tournament modal HTML not found or not loaded into DOM yet.');
    }
}

function closeTournamentModalLocal() {
    if (tournamentModal) {
        tournamentModal.style.display = 'none';
    }
}

// --- Form Submission Handler ---
async function handleCreateTournamentSubmit(event) {
    event.preventDefault();
    const form = event.target;
    const formData = new FormData(form);
    const tournamentData = Object.fromEntries(formData.entries());

    const auth = getFirebaseAuthService();
    const functions = getFunctionsService();

    if (!auth || !auth.currentUser) {
        alert("Error: No user signed in. Please sign in and try again.");
        return;
    }
    if (!functions) {
        alert("Error: Functions service is not available.");
        return;
    }

    let idToken = null;
    try {
        console.log("Requesting ID token refresh...");
        idToken = await auth.currentUser.getIdToken(true);
        console.log("ID token refreshed successfully. Token snippet:", idToken ? idToken.substring(0, 20) + "..." : "null");
    } catch (error) {
        console.error("handleCreateTournamentSubmit: Error retrieving ID token:", error);
        alert("Error: Could not verify your session. Please try signing out and signing back in.");
        return; 
    }

    if (!idToken) {
        console.error("handleCreateTournamentSubmit: ID token is null after refresh attempt.");
        alert("Critical Error: Failed to obtain user authentication token. Cannot proceed.");
        return;
    }

    if (tournamentData.maxPlayers) {
        tournamentData.maxPlayers = parseInt(tournamentData.maxPlayers, 10);
    }
    if (tournamentData.tournamentType === 'closed' && tournamentData.invitedPlayers) {
        tournamentData.invitedPlayers = tournamentData.invitedPlayers.split(',').map(id => id.trim()).filter(id => id);
    } else if (tournamentData.tournamentType === 'open') {
        delete tournamentData.invitedPlayers;
    }
    if (tournamentData.tournamentStartTime) {
        tournamentData.startTime = new Date(tournamentData.tournamentStartTime).toISOString();
    } else { delete tournamentData.startTime; }
    delete tournamentData.tournamentStartTime;

    if (tournamentData.tournamentEndTime) {
        tournamentData.endTime = new Date(tournamentData.tournamentEndTime).toISOString();
    } else { delete tournamentData.endTime; }
    delete tournamentData.tournamentEndTime;

    console.log("Submitting tournament data to Firebase (tourny-create-modal.js):", tournamentData);

    const submitButton = form.querySelector('button[type="submit"]');
    if (submitButton) submitButton.disabled = true;

    try {
        console.log("Calling Firebase Function (endpoint: initializeTournament_TEST) with tournament data...");
        // Call the renamed tournament creation function
        const createFunction = httpsCallable(functions, 'initializeTournament_TEST'); 
        const result = await createFunction(tournamentData);
        console.log("Tournament creation via initializeTournament_TEST endpoint successful:", result.data);
        alert(`Tournament "${tournamentData.tournamentName}" created successfully! ID: ${result.data.tournamentId} (via initializeTournament_TEST endpoint)`);
        closeTournamentModalLocal();
        refreshMyTournamentsView();
    } catch (error) {
        console.error("Error creating tournament:", error);
        alert(`Error creating tournament: ${error.message}`);
    } finally {
        if (submitButton) submitButton.disabled = false;
    }
}

// --- Load Modal HTML and Set Up Scripts/Listeners ---
function loadTournamentModalAndSetup() {
    if (!tournamentModalContainer) {
        console.error("Tournament modal container not found. Cannot load modal.");
        return Promise.reject("Tournament modal container missing.");
    }

    return fetch('/tournament-modal.html')
        .then(response => {
            if (!response.ok) throw new Error(`Failed to load tournament-modal.html: ${response.statusText}`);
            return response.text();
        })
        .then(data => {
            tournamentModalContainer.innerHTML = data;
            tournamentModal = document.getElementById('tournamentModal'); 

            if (!tournamentModal) {
                console.error("Tournament modal element (#tournamentModal) not found within loaded HTML.");
                return;
            }
            
            window.toggleTournamentPlayerFields = function() {
                const type = document.querySelector('input[name="tournamentType"]:checked')?.value;
                const maxPlayersContainer = document.getElementById('maxPlayersContainer');
                const invitedPlayersContainer = document.getElementById('invitedPlayersContainer');
                if (maxPlayersContainer) maxPlayersContainer.style.display = type === 'open' ? 'block' : 'none';
                if (invitedPlayersContainer) invitedPlayersContainer.style.display = type === 'closed' ? 'block' : 'none';
            };

            window.closeTournamentModal = closeTournamentModalLocal; 
            const closeButton = tournamentModal.querySelector('.close-button');
            if (closeButton) { 
                closeButton.removeEventListener('click', closeTournamentModalLocal);
                closeButton.addEventListener('click', closeTournamentModalLocal);
            }

            const createTournamentForm = document.getElementById('createTournamentForm');
            if (createTournamentForm) {
                createTournamentForm.removeEventListener('submit', handleCreateTournamentSubmit);
                createTournamentForm.addEventListener('submit', handleCreateTournamentSubmit);
            }

            if (typeof window.toggleTournamentPlayerFields === 'function') {
                 window.toggleTournamentPlayerFields();
            }

            console.log("Tournament modal HTML loaded and form handler attached.");
        })
        .catch(error => {
            console.error('Error loading or setting up tournament modal:', error);
            if (tournamentModalContainer) tournamentModalContainer.innerHTML = '<p>Error loading tournament creation tool. Please try again later.</p>';
        });
}

// --- Initialize Tournament Feature (called from index-main.js) ---
export async function initializeTournamentModal() {
    assignTournamentElements();

    if (createTournamentButton) {
        createTournamentButton.addEventListener('click', openTournamentModalLocal);
    }
    
    await loadTournamentModalAndSetup(); 
    console.log("tourny-create-modal.js: Initialized tournament modal functionality.");
} 